import React from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useOnboardingIntelligence } from '../hooks/useOnboardingIntelligence';
import { useGeneratedLabel } from '../hooks/useGeneratedLabel';
import {
    DANGER_RED_HOVER,
    HEADING_NAVY,
    TILE_ACCENT,
} from './onboardingIntelligence.styles';
import OnboardingIntelligenceTiles from './OnboardingIntelligenceTiles';
import PipelineBreakdownBar from './PipelineBreakdownBar';
import ActionItemsColumn from './ActionItemsColumn';
import ReadyForProductionColumn from './ReadyForProductionColumn';
import RecommendationsSection from './RecommendationsSection';

const TITLE_ID = 'onboarding-intelligence-title';

interface Props {
    open: boolean;
    onClose: () => void;
}

const Header: React.FC<{
    onClose: () => void;
    generatedLabel: string;
    totalSuppliers?: number;
    loading: boolean;
}> = ({ onClose, generatedLabel, totalSuppliers, loading }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '24px 24px 8px 24px',
        }}
    >
        <Box>
            <Typography
                id={TITLE_ID}
                variant='h5'
                sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 700,
                    color: HEADING_NAVY,
                }}
                data-testid='onboarding-intelligence-modal-title'
            >
                Onboarding Intelligence
            </Typography>
            <Typography
                variant='body2'
                sx={{ color: 'rgba(0,0,0,0.6)', marginTop: '4px' }}
                data-testid='onboarding-intelligence-generated-at'
            >
                Generated {generatedLabel}
                {totalSuppliers !== undefined && !loading
                    ? ` - Based on ${totalSuppliers} active suppliers`
                    : ''}
            </Typography>
        </Box>
        <IconButton
            onClick={onClose}
            aria-label='Close'
            data-testid='onboarding-intelligence-close'
            size='large'
            sx={{
                color: TILE_ACCENT.highPriority,
                '&:hover': {
                    color: DANGER_RED_HOVER,
                    backgroundColor: 'rgba(196, 69, 69, 0.08)',
                },
            }}
        >
            <HighlightOffIcon />
        </IconButton>
    </Box>
);

const OnboardingIntelligenceModal: React.FC<Props> = ({ open, onClose }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const stackColumns = useMediaQuery(theme.breakpoints.down('md'));

    const { data, loading, error, refresh, generatedAt } = useOnboardingIntelligence({
        enabled: open,
    });
    const generatedLabel = useGeneratedLabel(generatedAt, open);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth='lg'
            fullScreen={fullScreen}
            aria-labelledby={TITLE_ID}
            data-testid='onboarding-intelligence-modal'
            PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '12px' } }}
        >
            <Header
                onClose={onClose}
                generatedLabel={generatedLabel}
                totalSuppliers={data?.totalSuppliers}
                loading={loading}
            />
            {loading && (
                <LinearProgress
                    data-testid='onboarding-intelligence-loading'
                    aria-label='Loading onboarding intelligence'
                    sx={{ marginX: '24px' }}
                />
            )}
            <DialogContent sx={{ paddingTop: '8px' }}>
                {error && !data && (
                    <Box
                        role='alert'
                        data-testid='onboarding-intelligence-error'
                        sx={{ textAlign: 'center', padding: '24px' }}
                    >
                        <Typography color='error'>
                            Couldn&rsquo;t load onboarding intelligence.
                        </Typography>
                        <Typography
                            variant='caption'
                            sx={{ display: 'block', color: 'rgba(0,0,0,0.5)' }}
                        >
                            {error.message}
                        </Typography>
                        <Button
                            onClick={refresh}
                            sx={{ marginTop: '12px' }}
                            data-testid='onboarding-intelligence-error-retry'
                        >
                            Try Again
                        </Button>
                    </Box>
                )}

                {!error && data && data.totalSuppliers === 0 && (
                    <Box
                        sx={{ textAlign: 'center', padding: '32px' }}
                        data-testid='onboarding-intelligence-modal-empty'
                    >
                        <Typography variant='body1' sx={{ color: 'rgba(0,0,0,0.7)' }}>
                            No active suppliers yet. Invite suppliers to see onboarding
                            intelligence.
                        </Typography>
                    </Box>
                )}

                {!error && data && data.totalSuppliers > 0 && (
                    <Box>
                        <OnboardingIntelligenceTiles
                            totalSuppliers={data.totalSuppliers}
                            readyToGoLive={data.readyToGoLive}
                            highPriority={data.highPriority}
                        />
                        <Box sx={{ marginTop: '24px' }}>
                            <PipelineBreakdownBar pipeline={data.pipeline} />
                        </Box>
                        <Divider sx={{ marginY: '24px' }} />
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: stackColumns ? 12 : 7 }}>
                                <ActionItemsColumn
                                    actionItems={data.actionItems}
                                    unevaluated={data.unevaluated}
                                    onRefresh={refresh}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: stackColumns ? 12 : 5 }}>
                                <ReadyForProductionColumn
                                    items={data.readyList}
                                    onRefresh={refresh}
                                />
                            </Grid>
                        </Grid>
                        <Divider sx={{ marginY: '24px' }} />
                        <RecommendationsSection
                            notificationsOn={data.notificationsOn}
                            longestStalled={data.longestStalled}
                            readyList={data.readyList}
                            onRefresh={refresh}
                        />
                        {data.unevaluated > 0 && (
                            <Alert severity='info' sx={{ marginTop: '16px' }}>
                                {data.unevaluated} supplier{data.unevaluated === 1 ? '' : 's'}{' '}
                                couldn&rsquo;t be evaluated. Try again or contact support.
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default OnboardingIntelligenceModal;
